/**
 * Core Shipping Estimate Logic
 * Centralized module for calculating shipping estimates from product data
 */

/**
 * Get shipping data for a product (item type, override message, ETA date)
 * @param {Object} product - Product object with metafields
 * @returns {Object|null} Shipping data object or null
 */
function getProductShippingData(product) {
  if (!product) {
    return null;
  }
  
  if (!product.metafields || !Array.isArray(product.metafields)) {
    return null;
  }
  
  const shippingItemType = product.metafields.find(m => m.key === 'shipping_item_type' && m.namespace === 'custom')?.value || '';
  const overrideMessage = product.metafields.find(m => m.key === 'shipping_override_message' && m.namespace === 'custom')?.value || '';
  const etaDate = product.metafields.find(m => m.key === 'shipping_eta_date' && m.namespace === 'custom')?.value || '';
  
  return {
    shippingItemType,
    overrideMessage,
    etaDate,
    product: product // Include product object for shipping rules lookup
  };
}

/**
 * Get shipping rules JSON from shop metafield
 * Note: shipping_rules is a shop-level metafield, not product-level
 * @param {Object} product - Optional product object (kept for backward compatibility, not used)
 * @returns {Object|null} Parsed shipping rules JSON or null
 */
function getShippingRulesJson(product = null) {
  // Shipping rules are stored at shop level, loaded from theme.liquid into window.shippingRulesJson
  if (window.shippingRulesJson && typeof window.shippingRulesJson === 'object') {
    return window.shippingRulesJson;
  }
  
  return null;
}

/**
 * Calculate shipping estimate message from shipping data
 * @param {Object} shippingData - Shipping data object
 * @param {number} quantity - Product quantity
 * @param {string} context - Context: 'pdp' or 'cart'
 * @param {Object} product - Optional product object to get shipping rules from
 * @returns {string} Shipping estimate message
 */
function calculateShippingMessage(shippingData, quantity = 1, context = 'pdp', product = null) {
  if (!shippingData) {
    return 'Contact Customer Service for an estimated ship date.';
  }
  
  // Priority 1: Override message
  if (shippingData.overrideMessage && shippingData.overrideMessage.trim() !== '') {
    let message = shippingData.overrideMessage;
    
    // Handle ETA replacement for backorder
    if (shippingData.shippingItemType === 'backorder' && shippingData.etaDate && message.includes('{{eta}}')) {
      const etaDateObj = new Date(shippingData.etaDate);
      const formattedDate = etaDateObj.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      message = message.replace(/\{\{eta\}\}/g, formattedDate);
    }
    
    return message;
  }
  
  // Priority 2: Get shipping rules JSON from shop metafield
  const shippingRulesJson = getShippingRulesJson(product);
  
  if (!shippingRulesJson || !shippingRulesJson.types) {
    // Fallback to default message
    if (shippingRulesJson && shippingRulesJson.defaults) {
      return context === 'pdp' 
        ? (shippingRulesJson.defaults.default_pdp || shippingRulesJson.defaults.default_cart || 'Contact Customer Service for an estimated ship date.')
        : (shippingRulesJson.defaults.default_cart || shippingRulesJson.defaults.default_pdp || 'Contact Customer Service for an estimated ship date.');
    }
    return 'Contact Customer Service for an estimated ship date.';
  }
  
  // Find the type configuration using the shippingItemType as the key
  // If no item type provided, use defaults
  if (!shippingData.shippingItemType || shippingData.shippingItemType.trim() === '') {
    if (shippingRulesJson.defaults) {
      return context === 'pdp' 
        ? (shippingRulesJson.defaults.default_pdp || shippingRulesJson.defaults.default_cart || 'Contact Customer Service for an estimated ship date.')
        : (shippingRulesJson.defaults.default_cart || shippingRulesJson.defaults.default_pdp || 'Contact Customer Service for an estimated ship date.');
    }
    return 'Contact Customer Service for an estimated ship date.';
  }
  
  const itemType = shippingData.shippingItemType;
  
  // Try case-insensitive lookup - first try exact match, then lowercase
  let typeConfig = shippingRulesJson.types[itemType];
  
  if (!typeConfig) {
    // Try lowercase version
    const itemTypeLower = itemType.toLowerCase();
    typeConfig = shippingRulesJson.types[itemTypeLower];
    
    // If still not found, try to find by case-insensitive key match
    if (!typeConfig && shippingRulesJson.types) {
      const typeKeys = Object.keys(shippingRulesJson.types);
      const matchingKey = typeKeys.find(key => key.toLowerCase() === itemTypeLower);
      if (matchingKey) {
        typeConfig = shippingRulesJson.types[matchingKey];
        console.log(`Found type config using case-insensitive match: "${itemType}" matched "${matchingKey}"`);
      }
    }
  }
  
  if (!typeConfig) {
    console.warn(`Type config not found for itemType: "${itemType}". Available types:`, Object.keys(shippingRulesJson.types || {}));
    // Fallback to defaults if item type not found in JSON
    if (shippingRulesJson.defaults) {
      return context === 'pdp' 
        ? (shippingRulesJson.defaults.default_pdp || shippingRulesJson.defaults.default_cart || 'Contact Customer Service for an estimated ship date.')
        : (shippingRulesJson.defaults.default_cart || shippingRulesJson.defaults.default_pdp || 'Contact Customer Service for an estimated ship date.');
    }
    return 'Contact Customer Service for an estimated ship date.';
  }
  
  console.log(`Found type config for "${itemType}":`, typeConfig);
  
  let message = '';
  
  // Check if type has direct lead_days (no tiers)
  if (typeConfig.lead_days !== undefined && (!typeConfig.tiers || typeConfig.tiers.length === 0)) {
    message = context === 'pdp' 
      ? (typeConfig.pdp_msg || typeConfig.cart_msg || '')
      : (typeConfig.cart_msg || typeConfig.pdp_msg || '');
  } else if (typeConfig.tiers && typeConfig.tiers.length > 0) {
    // Find appropriate tier based on quantity
    let chosenTier = null;
    
    for (const tier of typeConfig.tiers) {
      if (tier.max_qty === null || tier.max_qty === undefined) {
        chosenTier = tier;
        break;
      } else if (quantity <= tier.max_qty) {
        chosenTier = tier;
        break;
      }
    }
    
    if (chosenTier) {
      // Use cart_msg for cart context, pdp_msg for PDP context
      if (context === 'cart') {
        message = chosenTier.cart_msg || chosenTier.pdp_msg || '';
      } else {
        message = chosenTier.pdp_msg || chosenTier.cart_msg || '';
      }
    }
  }
  
  // Handle ETA replacement for backorder
  if (shippingData.shippingItemType === 'backorder' && shippingData.etaDate && message.includes('{{eta}}')) {
    const etaDateObj = new Date(shippingData.etaDate);
    const formattedDate = etaDateObj.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    message = message.replace(/\{\{eta\}\}/g, formattedDate);
  }
  
  // Fallback to default message
  if (!message || message.trim() === '') {
    if (shippingRulesJson.defaults) {
      if (context === 'cart') {
        message = shippingRulesJson.defaults.default_cart || shippingRulesJson.defaults.default_pdp || '';
      } else {
        message = shippingRulesJson.defaults.default_pdp || shippingRulesJson.defaults.default_cart || '';
      }
    }
    
    if (!message || message.trim() === '') {
      message = 'Contact Customer Service for an estimated ship date.';
    }
  }
  
  return message;
}

/**
 * Fetch product by ID using GraphQL Storefront API
 * @param {string|number} productId - Product ID (GID format or numeric)
 * @param {string} productHandle - Optional product handle for REST API fallback
 * @returns {Promise<Object|null>} Product object with metafields or null
 */
async function fetchProductById(productId, productHandle = null) {
  try {
    // Validate productId
    if (!productId || productId === 'null' || productId === 'undefined' || productId === '') {
      console.warn('Invalid productId provided to fetchProductById:', productId);
      // Try to use handle instead
      if (productHandle) {
        return await fetchProductByHandleREST(productHandle);
      }
      return null;
    }
    
    // Get shop URL and access token
    const shopUrl = window.location.origin;
    const accessToken = window.shopifyStorefrontAccessTokens?.[shopUrl];
    
    if (!accessToken) {
      console.warn('Storefront access token not found, falling back to REST API');
      return await fetchProductByHandleREST(productHandle || productId);
    }
    
    // Convert numeric ID to GID format if needed
    let gid = productId;
    if (typeof productId === 'string' && !productId.startsWith('gid://')) {
      // Check if it's already a numeric string
      if (/^\d+$/.test(productId)) {
        gid = `gid://shopify/Product/${productId}`;
      } else {
        // It might be a handle, try REST API first
        if (productHandle) {
          return await fetchProductByHandleREST(productHandle);
        }
        // If productId looks like a handle, use REST API
        if (productId && productId.length > 0 && !/^\d+$/.test(productId)) {
          return await fetchProductByHandleREST(productId);
        }
        return null;
      }
    } else if (typeof productId === 'number') {
      gid = `gid://shopify/Product/${productId}`;
    } else if (!gid || gid === 'null' || gid === 'undefined' || gid === '' || gid === null || gid === undefined) {
      // Invalid GID format
      console.warn('Invalid GID format, skipping GraphQL call:', gid);
      if (productHandle) {
        return await fetchProductByHandleREST(productHandle);
      }
      return null;
    }
    
    // Final validation: ensure GID is in correct format before making GraphQL call
    if (!gid.startsWith('gid://shopify/Product/')) {
      console.warn('Invalid GID format for GraphQL, falling back to REST API:', gid);
      if (productHandle) {
        return await fetchProductByHandleREST(productHandle);
      }
      return null;
    }
    
    // Use GraphQL Storefront API to fetch product with metafields
    // Note: Storefront API requires identifiers for metafields
    const graphqlQuery = `
      query getProductById($id: ID!) {
        product(id: $id) {
          id
          title
          handle
          shippingItemType: metafield(namespace: "custom", key: "shipping_item_type") {
            value
          }
          shippingOverrideMessage: metafield(namespace: "custom", key: "shipping_override_message") {
            value
          }
          shippingEtaDate: metafield(namespace: "custom", key: "shipping_eta_date") {
            value
          }
        }
      }
    `;
    
    const variables = {
      id: gid
    };
    
    const apiVersion = '2024-07';
    const response = await fetch(`${shopUrl}/api/${apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': accessToken
      },
      body: JSON.stringify({
        query: graphqlQuery,
        variables: variables
      })
    });
    
    if (!response.ok) {
      throw new Error(`GraphQL request failed: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (data.errors) {
      console.error('GraphQL errors:');
      // Log the specific error for debugging
      if (data.errors.length > 0) {
        console.error('First GraphQL error:', data.errors[0].message);
      }
      // Fallback to REST API using handle if available
      if (productHandle) {
        console.log('Falling back to REST API with handle:', productHandle);
        return await fetchProductByHandleREST(productHandle);
      }
      return null;
    }
    
    if (data.data?.product) {
      const productNode = data.data.product;
      
      console.log('GraphQL product node:', productNode);
      
      // Transform metafields from GraphQL format to array format
      const metafields = [];
      
      if (productNode.shippingItemType?.value) {
        metafields.push({
          namespace: 'custom',
          key: 'shipping_item_type',
          value: productNode.shippingItemType.value,
          type: 'single_line_text_field'
        });
        console.log('Found shipping_item_type:', productNode.shippingItemType.value);
      } else {
        console.log('shipping_item_type metafield not found or empty in GraphQL');
      }
      
      if (productNode.shippingOverrideMessage?.value) {
        metafields.push({
          namespace: 'custom',
          key: 'shipping_override_message',
          value: productNode.shippingOverrideMessage.value,
          type: 'single_line_text_field'
        });
        console.log('Found shipping_override_message:', productNode.shippingOverrideMessage.value);
      } else {
        console.log('shipping_override_message metafield not found or empty in GraphQL');
      }
      
      if (productNode.shippingEtaDate?.value) {
        metafields.push({
          namespace: 'custom',
          key: 'shipping_eta_date',
          value: productNode.shippingEtaDate.value,
          type: 'date'
        });
        console.log('Found shipping_eta_date:', productNode.shippingEtaDate.value);
      } else {
        console.log('shipping_eta_date metafield not found or empty in GraphQL');
      }
      
      // If GraphQL didn't return metafields, try REST API as fallback
      if (metafields.length === 0 && productHandle) {
        console.log('No metafields from GraphQL, trying REST API fallback...');
        const restProduct = await fetchProductByHandleREST(productHandle);
        if (restProduct && restProduct.metafields && restProduct.metafields.length > 0) {
          console.log('Got metafields from REST API:', restProduct.metafields);
          return restProduct;
        }
      }
      
      const productData = {
        id: productNode.id,
        title: productNode.title,
        handle: productNode.handle,
        metafields: metafields
      };
      
      console.log('Returning product data with metafields:', productData);
      return productData;
    }
    
    // Fallback to REST API if GraphQL didn't return product
    if (productHandle) {
      console.log('GraphQL did not return product, trying REST API...');
      return await fetchProductByHandleREST(productHandle);
    }
    return null;
  } catch (error) {
    console.error(`Error fetching product ${productId} via GraphQL:`, error);
    // Fallback to REST API
    if (productHandle) {
      return await fetchProductByHandleREST(productHandle);
    }
    return null;
  }
}

/**
 * Fallback function to fetch product via REST API by handle
 * @param {string} handle - Product handle
 * @returns {Promise<Object|null>} Product object or null
 */
async function fetchProductByHandleREST(handle) {
  try {
    if (!handle) {
      return null;
    }
    
    console.log('Fetching product via REST API:', handle);
    
    // Try the standard products endpoint first
    const restResponse = await fetch(`${window.location.origin}/products/${handle}.json`);
    if (!restResponse.ok) {
      console.error(`Failed to fetch product ${handle}: ${restResponse.statusText}`);
      return null;
    }
    const restData = await restResponse.json();
    
    console.log('REST API response:', restData);
    
    if (!restData.product) {
      return null;
    }
    
    // REST API doesn't return metafields by default
    // Try to get metafields from the product object if they exist
    let metafields = [];
    
    // Check if metafields are in the product object (they usually aren't in REST API)
    if (restData.product.metafields && Array.isArray(restData.product.metafields) && restData.product.metafields.length > 0) {
      metafields = restData.product.metafields.map(mf => ({
        namespace: mf.namespace || 'custom',
        key: mf.key,
        value: mf.value,
        type: mf.type
      }));
      console.log('Found metafields in REST API response:', metafields);
    } else {
      // Metafields not in REST API response - this is expected
      // The REST API endpoint doesn't return metafields unless they're explicitly requested
      console.warn(`Product ${handle} fetched via REST API but metafields not in response (REST API doesn't return metafields by default)`);
      
      // Try to extract from product object directly if it has metafield properties
      // Some Shopify setups might have metafields in a different format
      if (restData.product.shipping_item_type) {
        metafields.push({
          namespace: 'custom',
          key: 'shipping_item_type',
          value: restData.product.shipping_item_type,
          type: 'single_line_text_field'
        });
      }
      if (restData.product.shipping_override_message) {
        metafields.push({
          namespace: 'custom',
          key: 'shipping_override_message',
          value: restData.product.shipping_override_message,
          type: 'single_line_text_field'
        });
      }
      if (restData.product.shipping_eta_date) {
        metafields.push({
          namespace: 'custom',
          key: 'shipping_eta_date',
          value: restData.product.shipping_eta_date,
          type: 'date'
        });
      }
    }
    
    const productData = {
      id: restData.product.id,
      title: restData.product.title,
      handle: restData.product.handle,
      metafields: metafields
    };
    
    console.log('Returning product from REST API:', productData);
    return productData;
  } catch (error) {
    console.error(`Error fetching product ${handle} via REST API:`, error);
    return null;
  }
}

/**
 * Get shipping estimate for a product by ID
 * @param {string|number} productId - Product ID
 * @param {number} quantity - Product quantity
 * @param {string} context - Context: 'pdp' or 'cart'
 * @param {string} productHandle - Optional product handle for REST API fallback
 * @returns {Promise<string>} Shipping estimate message
 */
async function getShippingEstimateForProduct(productId, quantity = 1, context = 'pdp', productHandle = null) {
  try {
    // Validate productId before making API call
    if (!productId || productId === 'null' || productId === 'undefined' || productId === '') {
      console.warn('Invalid productId provided to getShippingEstimateForProduct:', productId);
      // Try to use cart data if available
      if (window.cartDataFormatted && window.cartDataFormatted.cart && window.cartDataFormatted.cart.items && productHandle) {
        const cartItem = window.cartDataFormatted.cart.items.find(item => item.handle === productHandle);
        if (cartItem && cartItem.product && cartItem.product.metafields) {
          const shippingData = {
            shippingItemType: cartItem.product.metafields.shipping_item_type?.value || 
                             cartItem.product.metafields.shipping_item_type || '',
            overrideMessage: cartItem.product.metafields.shipping_override_message?.value || 
                            cartItem.product.metafields.shipping_override_message || '',
            etaDate: cartItem.product.metafields.shipping_eta_date?.value || 
                    cartItem.product.metafields.shipping_eta_date || ''
          };
          return calculateShippingMessage(shippingData, quantity, context, cartItem.product);
        }
      }
      return 'Contact Customer Service for an estimated ship date.';
    }
    
    const product = await fetchProductById(productId, productHandle);
    
    if (!product) {
      // Try to use cart data as fallback
      if (window.cartDataFormatted && window.cartDataFormatted.cart && window.cartDataFormatted.cart.items) {
        const cartItem = window.cartDataFormatted.cart.items.find(item => {
          return item.product_id === productId || item.handle === productHandle;
        });
        if (cartItem && cartItem.product && cartItem.product.metafields) {
          const shippingData = {
            shippingItemType: cartItem.product.metafields.shipping_item_type?.value || 
                             cartItem.product.metafields.shipping_item_type || '',
            overrideMessage: cartItem.product.metafields.shipping_override_message?.value || 
                            cartItem.product.metafields.shipping_override_message || '',
            etaDate: cartItem.product.metafields.shipping_eta_date?.value || 
                    cartItem.product.metafields.shipping_eta_date || ''
          };
          return calculateShippingMessage(shippingData, quantity, context, cartItem.product);
        }
      }
      return 'Contact Customer Service for an estimated ship date.';
    }
    
    const shippingData = getProductShippingData(product);
    
    if (!shippingData) {
      return 'Contact Customer Service for an estimated ship date.';
    }
    
    // Pass the product object from shippingData to calculateShippingMessage
    return calculateShippingMessage(shippingData, quantity, context, shippingData.product || product);
  } catch (error) {
    console.error('Error getting shipping estimate:', error);
    return 'Contact Customer Service for an estimated ship date.';
  }
}

// Make functions available globally
window.shippingEstimateCore = {
  getProductShippingData,
  calculateShippingMessage,
  fetchProductById,
  getShippingEstimateForProduct
};

